import { useFragment } from '@apollo/client';
import {
  Alert,
  Box,
  ContentBlock,
  Loader,
  Stack,
  Text,
} from 'braid-design-system';
import { useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { CheckoutPageError } from 'src/app/shared/components/CheckoutPageError';
import { log } from 'src/app/utils/logging';
import { useAnalyticsTracking } from 'src/app/v2/hooks/useAnalyticsTracking';
import { useDraftId } from 'src/app/v2/hooks/useDraftId';
import { generateIdempotencyKey } from 'src/app/v2/utils/generateJobIdempotencyKey';
import {
  DraftFragmentFragmentDoc,
  useCreateJobMutation,
} from 'src/graphql/generated';

import { FooterSection } from './sections/footer';
import { HeaderSection } from './sections/header/HeaderSection';

/**
 * REMINDER: Atmoic design
 * This is a page amd is divided into smaller sections (organisms)
 * Refer to /sections for components next level down
 * https://medium.com/galaxy-ux-studio/principles-of-atomic-design-7b03a30c3cb6
 */
export const PayAndPostPageV2 = () => {
  const { trackingAdapter } = useAnalyticsTracking();
  const draftId = useDraftId();
  const { data, complete } = useFragment({
    fragment: DraftFragmentFragmentDoc,
    from: {
      __typename: 'DraftJob',
      id: draftId,
    },
  });

  const [
    createJob,
    { data: job, loading: jobCreationInProgress, error: jobCreationError },
  ] = useCreateJobMutation({
    variables: {
      input: {
        draftId: draftId!,
        idempotencyKey: generateIdempotencyKey(data),
      },
    },
  });

  useEffect(() => {
    trackingAdapter.sendPageEvent();
  }, [trackingAdapter]);

  useEffect(() => {
    createJob();
  }, [createJob]);

  if (!complete) {
    return null;
  }

  if (jobCreationError) {
    return (
      <ContentBlock>
        <Box marginBottom="xxlarge" marginTop="large">
          <CheckoutPageError />
        </Box>
      </ContentBlock>
    );
  }

  return (
    <ErrorBoundary
      FallbackComponent={CheckoutPageError}
      onError={(error) => {
        log.error(
          'PayAndPostV2 | ErrorBoundary | error',
          {
            error,
            draftId,
            jobId: job?.createJob?.jobId,
          },
          true,
        );
      }}
    >
      <ContentBlock>
        <Box marginBottom="xxlarge" marginTop="large">
          <Stack space="medium">
            <HeaderSection />
            {!job && jobCreationInProgress && (
              <Stack space="none" align="center">
                <Loader />
              </Stack>
            )}
            {job && (
              <Alert tone="info">
                <Stack space="small">
                  <Text>Job created.</Text>
                  <Text>Revision ID: {job.createJob?.revisionId}</Text>
                  <Text>Idempotency Key: {job.createJob?.idempotencyKey}</Text>
                </Stack>
              </Alert>
            )}
            <FooterSection />
          </Stack>
        </Box>
      </ContentBlock>
    </ErrorBoundary>
  );
};
